import { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useForm, Controller } from "react-hook-form";
import { Stack, Group, Alert } from "@mantine/core";
import { HiExclamationCircle } from "react-icons/hi2";
import { toastService } from "../../../../providers";
import { useBankOptions } from "../../../../hooks/queries/useBanksQueries";
import { extractAxiosErrorMessage } from "../../../../services/utils/errorUtils";
import CustomSelect from "../../../ui/CustomSelect/CustomSelect";
import CustomTextInput from "../../../ui/CustomTextInput/CustomTextInput";
import CustomButton from "../../../ui/CustomButton/CustomButton";
// FUTURE RELEASE: CustomRadioGroup used for verification method selection — re-enable when MSISDN is supported
// import CustomRadioGroup from "../../../ui/CustomRadioGroup/CustomRadioGroup";
import { validationSchemas } from "../hooks/useAccountForm";

const { accountNumber: accountNumberValidator } =
  validationSchemas.bankAccountValidators;
// FUTURE RELEASE: MSISDN validator for phone number bank verification — re-enable when supported
// const { msisdn: msisdnValidator } = validationSchemas;

// FUTURE RELEASE: Verification method options — account_number and msisdn (phone number).
// Used by the CustomRadioGroup. Re-enable both entries when MSISDN bank verification is supported.
// const VERIFICATION_OPTIONS = [
//   {
//     value: "account_number",
//     label: "Account Number",
//     description: "Enter your bank account number directly",
//   },
//   {
//     value: "msisdn",
//     label: "Phone Number (MSISDN)",
//     description: "Use the mobile number linked to your bank account",
//   },
// ];

const labelStyle = {
  label: {
    fontWeight: 600,
    color: "#1f2937",
    fontSize: "0.9rem",
    marginBottom: "4px",
  },
};

const BankAccountStep1 = ({
  step1Draft,
  setStep1Draft,
  verifyBankAccount,
  // FUTURE RELEASE: verifyViaMsisdn — prop for phone number bank verification; re-enable when MSISDN is supported
  // verifyViaMsisdn,
  isVerifying,
  goToStep2,
  onClose,
}) => {
  const {
    banks,
    bankOptions,
    bankFilter,
    isBanksLoading: isLoading,
    isBanksError: isError,
  } = useBankOptions();
  const isEmpty = !isLoading && !isError && banks.length === 0;

  const [selectedBank, setSelectedBank] = useState(null);
  // FUTURE RELEASE: restore setVerificationMethod when the radio group is re-enabled
  const [verificationMethod] = useState(
    step1Draft.verificationMethod ?? "account_number",
  );

  const { control, handleSubmit, setValue, watch } = useForm({
    defaultValues: {
      accountNumber: step1Draft.accountNumber,
      // FUTURE RELEASE: MSISDN form field — re-enable when phone number bank verification is supported
      // msisdn: step1Draft.msisdn ?? "",
    },
    mode: "onTouched",
  });

  const accountNumber = watch("accountNumber");
  // FUTURE RELEASE: MSISDN field watch — re-enable when phone number bank verification is supported
  // const msisdn = watch("msisdn");

  // FUTURE RELEASE: Clears the opposite field when switching between account_number and msisdn.
  // Re-enable this effect when MSISDN verification is supported.
  // useEffect(() => {
  //   if (verificationMethod === "account_number") setValue("msisdn", "");
  //   else setValue("accountNumber", "");
  // }, [verificationMethod, setValue]);

  // Rehydrate selectedBank from store draft once banks are loaded
  useEffect(() => {
    if (step1Draft.selectedBankId && banks.length > 0 && !selectedBank) {
      const match = banks.find(
        (b) => b.id.toString() === step1Draft.selectedBankId.toString(),
      );
      if (match) setSelectedBank(match);
    }
  }, [banks, step1Draft.selectedBankId, selectedBank]);

  const handleBankChange = useCallback(
    (value) => {
      if (!value) {
        setSelectedBank(null);
        setValue("accountNumber", "");
        // FUTURE RELEASE: clear MSISDN field on bank deselect — re-enable with phone verification
        // setValue("msisdn", "");
        return;
      }
      const bank = banks.find((b) => b.id.toString() === value);
      setSelectedBank(bank ?? null);
    },
    [banks, setValue],
  );

  const handleVerifySuccess = useCallback(
    (
      response,
      accNum /*, phone — FUTURE RELEASE: pass phone when MSISDN verification is re-enabled */,
    ) => {
      setStep1Draft({
        selectedBankId: selectedBank.id.toString(),
        selectedBankName: selectedBank.name,
        selectedBankCode: selectedBank.swiftCode,
        accountNumber: verificationMethod === "account_number" ? accNum : "",
        // FUTURE RELEASE: persist MSISDN in draft — re-enable when phone verification is supported
        // msisdn: verificationMethod === "msisdn" ? phone : "",
        verificationMethod,
      });
      toastService.success("Account verified successfully");
      goToStep2(response);
    },
    [selectedBank, verificationMethod, setStep1Draft, goToStep2],
  );

  const handleVerifyError = useCallback((error) => {
    toastService.error(
      extractAxiosErrorMessage(error, "Account verification failed"),
    );
  }, []);

  // FUTURE RELEASE: restore `msisdn: phone` to the destructure when MSISDN verification is re-enabled
  const onVerify = handleSubmit(
    ({ accountNumber: accNum /*, msisdn: phone */ }) => {
      if (verificationMethod === "account_number") {
        verifyBankAccount(
          {
            bankName: selectedBank.name,
            accountNumber: accNum.trim(),
            swiftCode: selectedBank.swiftCode,
            branchCode: selectedBank.code,
            type: "BANK",
          },
          {
            onSuccess: (res) => handleVerifySuccess(res, accNum),
            onError: handleVerifyError,
          },
        );
      }
      // FUTURE RELEASE: MSISDN verification path — sends phone number to the bank lookup API.
      // Re-enable verifyViaMsisdn prop and the else branch when phone number bank verification is supported.
      // else {
      //   verifyViaMsisdn(
      //     {
      //       bankName: selectedBank.name,
      //       msisdn: phone.trim(),
      //       bankCode: selectedBank.swiftCode,
      //       branchCode: selectedBank.code,
      //       type: "MSISDN",
      //     },
      //     {
      //       onSuccess: (res) => handleVerifySuccess(res, accNum, phone),
      //       onError: handleVerifyError,
      //     },
      //   );
      // }
    },
  );

  // FUTURE RELEASE: Restore ternary (account_number vs msisdn) when MSISDN verification is re-enabled:
  // (verificationMethod === "account_number"
  //   ? accountNumberValidator(accountNumber ?? "") === null
  //   : msisdnValidator(msisdn ?? "") === null)
  const canVerify =
    !!selectedBank &&
    !isVerifying &&
    accountNumberValidator(accountNumber ?? "") === null;

  return (
    <Stack style={{ flex: 1 }}>
      <CustomSelect
        label="Select Bank"
        placeholder={isLoading ? "Loading banks..." : "Search by name or code"}
        data={bankOptions}
        searchable
        lockOnSelect
        filter={bankFilter}
        value={selectedBank ? selectedBank.id.toString() : null}
        onChange={handleBankChange}
        disabled={isLoading || isError || isEmpty || isVerifying}
        withAsterisk
        styles={labelStyle}
        comboboxProps={{ withinPortal: false }}
      />

      {isError && (
        <Alert
          icon={<HiExclamationCircle size={16} />}
          color="red"
          variant="light"
        >
          Failed to load banks. Please close and try again.
        </Alert>
      )}

      {isEmpty && (
        <Alert
          icon={<HiExclamationCircle size={16} />}
          color="yellow"
          variant="light"
        >
          No banks available at the moment.
        </Alert>
      )}

      {selectedBank && (
        <Stack gap="sm">
          <CustomTextInput
            label="Bank Name"
            value={selectedBank.name}
            disabled
            styles={labelStyle}
            radius="xl"
          />

          <CustomTextInput
            label="SWIFT Code"
            value={selectedBank.swiftCode}
            disabled
            styles={labelStyle}
            radius="xl"
          />
        </Stack>
      )}

      {/* FUTURE RELEASE: Verification method radio group — lets the user pick between account number
           and phone number (MSISDN). Re-enable when MSISDN bank verification is supported. */}
      {/* {selectedBank && (
        <CustomRadioGroup
          label="Verification Method"
          value={verificationMethod}
          onChange={setVerificationMethod}
          options={VERIFICATION_OPTIONS}
          disabled={isVerifying}
          styles={{ label: labelStyle.label }}
        />
      )} */}

      {selectedBank && verificationMethod === "account_number" && (
        <Controller
          name="accountNumber"
          control={control}
          rules={{ validate: (v) => accountNumberValidator(v) ?? true }}
          render={({ field, fieldState }) => (
            <CustomTextInput
              label="Account Number"
              placeholder="Enter account number"
              radius="xl"
              styles={labelStyle}
              withAsterisk
              disabled={isVerifying}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={fieldState.error?.message}
            />
          )}
        />
      )}

      {/* FUTURE RELEASE: Phone number input for MSISDN bank verification.
           Shown when the user selects "Phone Number (MSISDN)" as the verification method.
           Re-enable when MSISDN bank verification is supported. */}
      {/* {selectedBank && verificationMethod === "msisdn" && (
        <Controller
          name="msisdn"
          control={control}
          rules={{ validate: (v) => msisdnValidator(v) ?? true }}
          render={({ field, fieldState }) => (
            <CustomTextInput
              label="Phone Number"
              placeholder="e.g. 254711223344"
              radius="xl"
              styles={labelStyle}
              withAsterisk
              disabled={isVerifying}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={fieldState.error?.message}
            />
          )}
        />
      )} */}

      <Group justify="flex-end" mt="auto">
        <CustomButton
          variant="default"
          onClick={onClose}
          disabled={isVerifying}
        >
          Cancel
        </CustomButton>
        <CustomButton
          onClick={onVerify}
          loading={isVerifying}
          disabled={!canVerify}
        >
          Verify Account
        </CustomButton>
      </Group>
    </Stack>
  );
};

BankAccountStep1.propTypes = {
  step1Draft: PropTypes.shape({
    selectedBankId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    selectedBankName: PropTypes.string,
    selectedBankCode: PropTypes.string,
    accountNumber: PropTypes.string,
    verificationMethod: PropTypes.string,
    // FUTURE RELEASE: msisdn — re-enable when phone number bank verification is supported
    // msisdn: PropTypes.string,
  }).isRequired,
  setStep1Draft: PropTypes.func.isRequired,
  verifyBankAccount: PropTypes.func.isRequired,
  // FUTURE RELEASE: verifyViaMsisdn — re-enable when MSISDN bank verification is supported
  // verifyViaMsisdn: PropTypes.func.isRequired,
  isVerifying: PropTypes.bool.isRequired,
  goToStep2: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default BankAccountStep1;
